import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, PageHeader } from 'antd';
import { generatePath, RouteComponentProps } from 'react-router';

import * as S from './CulturesList.styles';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { RouteConstants } from 'app/routes';
import { useCulturesSlice } from '../../slice';
import {
  selectCulturesListLoading,
  selectCompaniesCultures,
  selectListCultures,
} from '../../slice/selectors';
import PermissionsWrapper from 'app/components/PermissionsWrapper/PermissionsWrapper';
import { PermissionsEnum } from 'app/containers/User/slice/types';
import CompaniesCulturesTables from '../../parts/CompaniesCulturesTables/CompaniesCulturesTables';
import CulturesTable from '../../parts/CulturesTable/CulturesTable';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';

interface IProps extends RouteComponentProps {}

const CulturesList = ({ history }: IProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { actions } = useCulturesSlice();

  const envId = useSelector(selectSelectedEnvId);
  const loading = useSelector(selectCulturesListLoading);
  const culturesList = useSelector(selectListCultures);
  const companiesWithCultures = useSelector(selectCompaniesCultures);
  const seasonId = useSelector(selectSelectedSeasonId);

  useEffect(() => {
    if (envId && seasonId) {
      dispatch(
        actions.getCulturesList({
          hiikoEnvironmentId: envId,
          cultureId: null,
          seasonId,
        }),
      );
    }
  }, [envId, actions, dispatch, seasonId]);

  if (!envId) {
    return null;
  }

  const totalCulturesArea = culturesList.reduce((prevItem, nextItem) => {
    return (prevItem += Number(nextItem.area));
  }, 0);

  return (
    <>
      <PageHeader
        className="page-header"
        title={t('Cultures.List.Title', 'Cultures')}
      />
      {loading ? (
        <S.ContentWrapper>
          <S.Spinner tip="Loading..." size="large" />
        </S.ContentWrapper>
      ) : (
        <S.ContentWrapper>
          {culturesList.length > 0 && (
            <S.StyledCard
              title={t('Cultures.List.Table.MainTitle', 'Cultivated cultures')}
              bordered={false}
              extra={
                <PermissionsWrapper
                  requiredEditPermissions={[
                    PermissionsEnum.cultureAdministration,
                  ]}
                >
                  <Button
                    type="primary"
                    onClick={() =>
                      history.push(
                        generatePath(RouteConstants.cultures.createCulture, {
                          envId,
                        }),
                      )
                    }
                  >
                    {t('Cultures.List.CreateCulture', 'Add Culture')}
                  </Button>
                </PermissionsWrapper>
              }
            >
              <CulturesTable
                culturesArr={culturesList}
                totalArea={totalCulturesArea}
              />
            </S.StyledCard>
          )}
          <CompaniesCulturesTables companies={companiesWithCultures} />
          {culturesList.length <= 0 && (
            <S.EmptyStateWrapper>
              <S.InfoIcon />
              <S.EmptyStateText>
                {t(
                  'Cultures.List.NoCultures',
                  'You currently have no cultures added',
                )}
              </S.EmptyStateText>
              <Button
                type="primary"
                onClick={() =>
                  history.push(
                    generatePath(RouteConstants.cultures.createCulture, {
                      envId,
                    }),
                  )
                }
              >
                {t('Cultures.List.CreateCulture', 'Add Culture')}
              </Button>
            </S.EmptyStateWrapper>
          )}
        </S.ContentWrapper>
      )}
    </>
  );
};

export default CulturesList;
